import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Users, ClipboardCheck, FileSpreadsheet, Award, Shield, Star, ChevronRight, Building2, Brain, Trophy } from 'lucide-react';

const JudgingProcess = () => {
  const processSteps = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "Public Nominations",
      date: "October 2024",
      description: "The nomination period begins, allowing the public to put forward deserving candidates across all categories.",
      details: [
        "Open to all industry participants",
        "Multiple category nominations allowed",
        "Comprehensive submission guidelines provided",
        "Support team available for assistance"
      ]
    },
    {
      icon: <ClipboardCheck className="h-8 w-8" />,
      title: "Nominee Applications",
      date: "April 2025",
      description: "All nominees are contacted to submit comprehensive applications detailing their achievements and innovations.",
      details: [
        "Detailed achievement documentation",
        "Supporting evidence collection",
        "Performance metrics submission",
        "Innovation impact statements"
      ]
    },
    {
      icon: <Scale className="h-8 w-8" />,
      title: "Evaluation Period",
      date: "May 2025",
      description: "Our expert panel conducts thorough research and holds extensive consultations over a 4-week period.",
      details: [
        "Multi-stage evaluation process",
        "Peer review sessions",
        "Technical assessment",
        "Impact measurement"
      ]
    },
    {
      icon: <FileSpreadsheet className="h-8 w-8" />,
      title: "Independent Audit",
      date: "June 2025",
      description: "A top 5 audit firm conducts a comprehensive audit of all results before the finalists are announced.",
      details: [
        "Process verification",
        "Scoring validation",
        "Compliance check",
        "Results certification"
      ]
    },
    {
      icon: <Trophy className="h-8 w-8" />,
      title: "Winners Announcement",
      date: "November 25th, 2025",
      description: "Winners are revealed exclusively at the prestigious gala dinner ceremony.",
      details: [
        "Live announcement ceremony",
        "Industry-wide recognition",
        "Media coverage",
        "Networking opportunities"
      ]
    }
  ];

  const criteria = [
    {
      icon: <Star className="h-6 w-6" />,
      title: "Innovation",
      description: "Evaluation of technological advancement and creative problem-solving"
    },
    {
      icon: <Building2 className="h-6 w-6" />,
      title: "Market Impact",
      description: "Assessment of market penetration and customer adoption"
    },
    {
      icon: <Brain className="h-6 w-6" />,
      title: "Leadership",
      description: "Vision, strategy, and industry influence"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Sustainability",
      description: "Long-term viability and environmental consideration"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              Our Judging
              <span className="block" style={{ color: '#C1B3A3' }}>
                Process
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              A Rigorous and Transparent Evaluation System
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-4">The Journey to Excellence</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our comprehensive evaluation process ensures fair and thorough assessment of all nominations
            </p>
          </motion.div>

          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                  {/* Timeline Line */}
                  {index !== processSteps.length - 1 && (
                    <div className="absolute left-12 md:left-[7.5rem] top-24 bottom-0 w-0.5 bg-gradient-to-b from-[#C1B3A3] to-transparent" />
                  )}

                  {/* Icon and Date */}
                  <div className="flex-shrink-0 w-24 md:w-60 text-center md:text-right">
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white shadow-xl border-2" 
                         style={{ borderColor: '#C1B3A3' }}>
                      <div style={{ color: '#C1B3A3' }}>{step.icon}</div>
                    </div>
                    <div className="mt-4 font-medium text-gray-500">{step.date}</div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A1A1A' }}>{step.title}</h3>
                    <p className="text-gray-600 mb-6">{step.description}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <ChevronRight className="h-4 w-4" style={{ color: '#C1B3A3' }} />
                          <span className="text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Evaluation Criteria */}
      <section className="py-20" style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-4">Evaluation Criteria</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our judging panel evaluates each nomination based on four key pillars
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {criteria.map((criterion, index) => (
              <motion.div
                key={criterion.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                     style={{ backgroundColor: 'rgba(193, 179, 163, 0.2)' }}>
                  <div style={{ color: '#C1B3A3' }}>{criterion.icon}</div>
                </div>
                <h3 className="text-xl font-bold mb-4">{criterion.title}</h3>
                <p className="text-gray-600">{criterion.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrity Statement */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Award className="h-12 w-12 mx-auto mb-6" style={{ color: '#C1B3A3' }} />
            <h2 className="text-3xl font-bold mb-6">Our Commitment to Integrity</h2>
            <p className="text-lg text-gray-300 mb-8">
              The SA FinTech Awards maintains the highest standards of integrity and transparency 
              throughout the entire judging process. Our commitment to fairness and impartiality 
              is unwavering.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-white/5 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#C1B3A3' }}>Independence</h3>
                <p className="text-gray-300">
                  All judges sign strict confidentiality agreements and declare any potential conflicts 
                  of interest. Decisions are made independently and collectively reviewed.
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#C1B3A3' }}>Transparency</h3>
                <p className="text-gray-300">
                  Our evaluation criteria and process are clearly documented and available to all 
                  participants. We provide feedback to all nominees upon request.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Judges Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#C1B3A3' }}>
              Meet Our Distinguished Judges
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our panel of experts brings together decades of experience in finance, technology, and innovation
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              {
                name: "Ashlin Perumall",
                title: "Partner, Baker McKenzie",
                image: "https://titc.io/wp-content/uploads/2025/02/ashkin.jpeg",
                description: "Ashlin Perumall is a partner in Baker McKenzie's corporate M&A and IPTech practice groups in Johannesburg."
              },
              {
                name: "Wendy Pienaar",
                title: "Strategy Lead, Deloitte Africa",
                image: "https://titc.io/wp-content/uploads/2025/02/wendy.jpeg",
                description: "Wendy is currently the Strategy Lead for Deloitte Africa including the Innovation and Ventures portfolio. Wendy has a passion for the African Fintech space and has helped many move through the ideate, incubate industrialise, scale and mature phases."
              },
              {
                name: "Arno Von Helden",
                title: "CIO Transactional Products & Solutions, Investec",
                image: "https://titc.io/wp-content/uploads/2025/02/arono.jpeg",
                description: "Prior to joining Investec, Arono was the CEO of Shyft by Standard Bank and comes with a formidable track record of developing and managing innovative digital platform businesses."
              },
              {
                name: "Angela itzikowitz",
                title: "Professor, ENS",
                image: "https://www.ensafrica.com/Uploads/People/1055_Angela%20Itzikowitz%20AI002.jpg?width=250&height=310&scale=upscalecanvas&mode=crop&quality=90&format=jpg",
                description: "Commerical lawyer with extensive experience in fintech."
              },
              {
                name: "Lee Naik",
                title: "CEO, TransUnion Africa",
                image: "https://titc.io/wp-content/uploads/2025/02/lee.jpeg",
                description: "Lee is recognized as one of the continent's leading authorities on how technology can be used to solve problems that matter."
              },
              {
                name: "Chris Wood",
                title: "Managing Executive, ABSA Group",
                image: "https://titc.io/wp-content/uploads/2025/02/chris.jpg",
                description: "Banker, FinTech Executive and expert in Payments across Africa."
              },
              {
                name: "Cleola (Soundy) Kunene",
                title: "Head of SME Development, JSE Ltd",
                image: "https://titc.io/wp-content/uploads/2025/02/1732961494986.jpeg",
                description: "Award Winning small business growth and investment expert, from Start-Up to Scale-Up"
              }
            ].map((judge, index) => (
              <motion.div
                key={judge.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-lg bg-white shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-w-1 aspect-h-1">
                  <img
                    src={judge.image}
                    alt={judge.name}
                    className="w-full h-48 object-cover object-center transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-base font-bold text-white mb-1 line-clamp-1">{judge.name}</h3>
                  <p className="text-sm font-medium text-gray-300 mb-2 line-clamp-2">{judge.title}</p>
                  <p className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 line-clamp-3">
                    {judge.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default JudgingProcess;